import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Users, Mail, Phone, Building, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const iconFor = { customer: Users, insurance: Building, body_shop: Building, technician: Wrench };

export default function CRM() {
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "customer", name: "", email: "", phone: "", address: "", notes: "" });

  const load = () => {
    const q = filter ? `?type=${filter}` : "";
    api.get(`/crm/contacts${q}`).then((r) => setContacts(r.data));
  };

  useEffect(() => { load(); }, [filter]);

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/crm/contacts", form);
    toast.success("Contact added");
    setOpen(false);
    setForm({ type: "customer", name: "", email: "", phone: "", address: "", notes: "" });
    load();
  };

  const types = ["", "customer", "insurance", "body_shop", "technician"];

  return (
    <div className="space-y-6 animate-slide-up" data-testid="crm-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Contacts</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">CRM</h1>
          <p className="text-sm text-slate-400 mt-1.5">All customers, insurers, body shops, and technicians.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-contact-btn" className="bg-blue-600 hover:bg-blue-500"><Plus className="w-4 h-4 mr-2" />Add Contact</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B1120] border-slate-800">
            <DialogHeader><DialogTitle>New Contact</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Type</Label>
                <select data-testid="contact-type" className="mt-1.5 w-full bg-[#030712] border border-slate-800 rounded-lg p-2 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="customer">Customer</option>
                  <option value="insurance">Insurance</option>
                  <option value="body_shop">Body Shop</option>
                  <option value="technician">Technician</option>
                </select>
              </div>
              <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Name</Label><Input data-testid="contact-name" className="mt-1.5 bg-[#030712] border-slate-800" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Email</Label><Input className="mt-1.5 bg-[#030712] border-slate-800" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Phone</Label><Input className="mt-1.5 bg-[#030712] border-slate-800" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <Button data-testid="save-contact-btn" onClick={create} className="bg-blue-600 hover:bg-blue-500 w-full">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2">
        {types.map((t) => (
          <button key={t || "all"} data-testid={`crm-filter-${t || "all"}`} onClick={() => setFilter(t)}
            className={`px-3 py-2 text-xs uppercase tracking-wide font-bold rounded-lg border transition-colors ${
              filter === t ? "bg-blue-600/20 text-blue-300 border-blue-500/40" : "bg-slate-900/40 text-slate-400 border-slate-800"
            }`}>{t ? t.replace("_", " ") : "All"}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {contacts.map((c) => {
          const Icon = iconFor[c.type] || Users;
          return (
            <div key={c.id} className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 hover:border-blue-500/40 transition-colors" data-testid={`contact-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <Icon className="w-5 h-5 text-blue-400" />
                <Badge className="bg-slate-800 text-slate-300 border-slate-700 text-[10px] uppercase tracking-wider">{c.type.replace("_", " ")}</Badge>
              </div>
              <div className="font-display text-lg font-medium">{c.name}</div>
              {c.email && <div className="text-xs text-slate-400 mt-1.5 flex items-center gap-1.5"><Mail className="w-3 h-3" />{c.email}</div>}
              {c.phone && <div className="text-xs text-slate-400 mt-1 font-mono flex items-center gap-1.5"><Phone className="w-3 h-3" />{c.phone}</div>}
              {c.notes && <div className="text-xs text-slate-500 mt-3 leading-relaxed border-t border-slate-800 pt-3">{c.notes}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
